import React, { useContext, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import { isSeen, markSeen } from '../utils/onboardingSeen';

const GUIDE_KEY = 'business_setup_guide';

const FIELDS = [
    {
        target: '#onboarding-field-logo',
        titleKey: 'onboardingTour.setupBusinessGuide.logo.title',
        contentKey: 'onboardingTour.setupBusinessGuide.logo.content',
    },
    {
        target: '#business_name',
        titleKey: 'onboardingTour.setupBusinessGuide.name.title',
        contentKey: 'onboardingTour.setupBusinessGuide.name.content',
    },
    {
        target: '#onboarding-field-slug',
        titleKey: 'onboardingTour.setupBusinessGuide.slug.title',
        contentKey: 'onboardingTour.setupBusinessGuide.slug.content',
    },
    {
        target: '#onboarding-field-fiscal-id',
        titleKey: 'onboardingTour.setupBusinessGuide.fiscalId.title',
        contentKey: 'onboardingTour.setupBusinessGuide.fiscalId.content',
    },
    {
        target: '#onboarding-save-business',
        titleKey: 'onboardingTour.setupBusinessGuide.save.title',
        contentKey: 'onboardingTour.setupBusinessGuide.save.content',
    },
];

/**
 * Guía paso a paso (campo por campo) de la sección "Información del Negocio", solo mientras
 * el negocio no tenga su primer guardado (`user.is_setup_complete === false`). Complementa al
 * spotlight global de OnboardingTour.js: ese lleva al usuario hasta este botón/página; esta
 * guía, ya en la página, lo lleva campo por campo hasta "Guardar Cambios". Se marca como vista
 * en localStorage al cerrarla, igual que el resto del onboarding — no depende de cómo llegó a
 * la página (banner o navegación directa), solo de si ya completó el primer guardado.
 */
const BusinessSetupGuide = () => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState(false);

    const shouldRun = !!user && user.role === 'owner' && user.is_setup_complete === false
        && !dismissed && !isSeen(user.id, GUIDE_KEY);

    if (!shouldRun) return null;

    const steps = FIELDS.map((field) => ({
        target: field.target,
        content: (
            <div>
                <strong>{t(field.titleKey)}</strong>
                <p className="mb-0 mt-1">{t(field.contentKey)}</p>
            </div>
        ),
        // v3 no tiene disableBeacon (gotcha de la tarea 030) — skipBeacon abre el tooltip
        // directo, sin esperar un clic en el beacon, ideal para un tour guiado paso a paso.
        skipBeacon: true,
        placement: 'bottom',
    }));

    const handleCallback = (data) => {
        if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
            markSeen(user.id, GUIDE_KEY);
            setDismissed(true);
        }
    };

    return (
        <Joyride
            steps={steps}
            run
            continuous
            showProgress
            showSkipButton
            locale={{
                back: t('common.back'),
                next: t('common.next'),
                last: t('common.finish'),
                close: t('common.gotIt'),
                skip: t('common.skip'),
            }}
            styles={{ options: { primaryColor: '#0d6efd', zIndex: 10000 } }}
            callback={handleCallback}
        />
    );
};

export default BusinessSetupGuide;
